/**
 * Markdown Table Formatter
 *
 * Utility function to format data as a markdown table with properly spaced columns.
 * Analyzes column widths based on headers and data values, then generates a
 * well-formatted markdown table.
 */

export interface MarkdownTableOptions {
  /** Minimum column width in characters (default: 3) */
  minColumnWidth?: number;
  /** Maximum column width in characters (default: 50) */
  maxColumnWidth?: number;
  /** Alignment strategy: 'left', 'right', 'center', or 'auto' (default: 'left') */
  align?: 'left' | 'right' | 'center' | 'auto';
}

/**
 * Format data as a markdown table with properly spaced columns
 *
 * @param headers - Array of column header strings
 * @param rows - Array of row arrays, where each row contains cell values as strings
 * @param options - Optional formatting options
 * @returns Formatted markdown table string
 */
export function formatMarkdownTable(
  headers: string[],
  rows: string[][],
  options: MarkdownTableOptions = {}
): string {
  const { minColumnWidth = 3, maxColumnWidth = 50, align = 'left' } = options;

  if (headers.length === 0) {
    return '';
  }

  // Calculate column widths
  const columnWidths = headers.map((header, colIndex) => {
    // Start with header width
    let maxWidth = header.length;

    // Check all rows for this column
    for (const row of rows) {
      // Convert to string to match how we'll format it later
      const cellValue = String(row[colIndex] || '');
      maxWidth = Math.max(maxWidth, cellValue.length);
    }

    // Apply constraints
    return Math.max(minColumnWidth, Math.min(maxWidth, maxColumnWidth));
  });

  // Helper to pad a string to a specific width
  const padCell = (
    value: string,
    width: number,
    alignment: 'left' | 'right' | 'center'
  ): string => {
    const truncated = value.length > width ? value.slice(0, width) : value;
    const padding = width - truncated.length;

    switch (alignment) {
      case 'right': {
        return ' '.repeat(padding) + truncated;
      }
      case 'center': {
        const leftPad = Math.floor(padding / 2);
        const rightPad = padding - leftPad;
        return ' '.repeat(leftPad) + truncated + ' '.repeat(rightPad);
      }
      default: {
        return `${truncated}${' '.repeat(padding)}`;
      }
    }
  };

  // Determine alignment for each column
  const columnAlignments: ('left' | 'right' | 'center')[] = headers.map((_, colIndex) => {
    if (align === 'auto') {
      // Auto-detect: if all values in column are numeric (or look numeric), right-align
      const allNumeric = rows.every((row) => {
        const cell = String(row[colIndex] || '');
        // Check if it looks like a number (allows for currency symbols, commas, decimals)
        return /^[\s$]*-?\d[\d,.]*%?$/.test(cell.trim()) || cell.trim() === '';
      });
      return allNumeric ? 'right' : 'left';
    }
    return align;
  });

  // Build header row
  const headerRow = `| ${headers.map((header, i) => padCell(header, columnWidths[i], 'left')).join(' | ')} |`;

  // Build separator row (simple dashes, no alignment indicators)
  // Match the exact format of header/data rows: | padded-content |
  const separatorRow =
    '|' +
    columnWidths
      .map((width) => {
        const dashes = '-'.repeat(width);
        // Pad to match header/data row format: space + content + space
        return ` ${dashes} |`;
      })
      .join('');

  // Build data rows
  const dataRows = rows.map((row) => {
    return (
      '| ' +
      row
        .map((cell, i) => {
          // Convert to string to handle numbers and other types consistently
          const cellValue = String(cell || '');
          return padCell(cellValue, columnWidths[i], columnAlignments[i]);
        })
        .join(' | ') +
      ' |'
    );
  });

  // Combine all parts
  return [headerRow, separatorRow, ...dataRows].join('\n');
}
